import { LoadingPage } from '@chiroup/components/LoadingPage';
import { DataTable } from '@chiroup/components/ui/data-table';
import { LinkIcon, MousePointerClickIcon, TrophyIcon, UsersIcon } from 'lucide-react';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUserReferrals } from '../../../hooks/useReferrals';
import { buildReferralColumns, REFERRAL_STATUS_META } from '../../referrals/referralColumns';

const StatCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, icon, color }) => (
  <div className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/60 p-4">
    <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${color}`}>{icon}</div>
    <div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  </div>
);

/** Per-user referral stats for the admin user-detail Referrals tab. */
const UserReferrals: React.FC = () => {
  const { userId } = useParams();
  const { data, isFetching } = useUserReferrals(userId);

  const columns = useMemo(() => buildReferralColumns(false), []);
  const statusOptions = useMemo(
    () =>
      Object.entries(REFERRAL_STATUS_META).map(([value, meta]) => ({
        label: meta.label,
        value,
      })),
    []
  );

  if (!data && isFetching) {
    return <LoadingPage />;
  }

  const conversions = (data?.referrals ?? []).filter(
    row => row.status === 'closeWon' || row.status === 'churned'
  ).length;

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <StatCard
          label="Referral code"
          value={data?.link?.code ?? '—'}
          icon={<LinkIcon className="h-5 w-5 text-white" />}
          color="bg-blue-500"
        />
        <StatCard
          label="Link clicks"
          value={(data?.clicks ?? 0).toLocaleString()}
          icon={<MousePointerClickIcon className="h-5 w-5 text-white" />}
          color="bg-cyan-500"
        />
        <StatCard
          label="Trials started"
          value={(data?.referrals?.length ?? 0).toLocaleString()}
          icon={<UsersIcon className="h-5 w-5 text-white" />}
          color="bg-indigo-500"
        />
        <StatCard
          label="Converted"
          value={conversions.toLocaleString()}
          icon={<TrophyIcon className="h-5 w-5 text-white" />}
          color="bg-green-500"
        />
      </div>

      <DataTable
        columns={columns}
        data={data?.referrals ?? []}
        pagination={{ pageSize: 25, pageSizeOptions: [25, 50, 100] }}
        toolbar={{
          search: { column: 'referred', placeholder: 'Search referred users...' },
          filters: [{ column: 'status', title: 'Status', options: statusOptions }],
        }}
        striped
        emptyState={
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-dark-gray-400">
            <UsersIcon className="h-12 w-12 mb-4 text-gray-300 dark:text-dark-gray-600" />
            <p className="text-sm font-medium">
              {data?.link ? 'No referrals yet' : 'This user has not generated a referral link yet'}
            </p>
          </div>
        }
      />
    </div>
  );
};

export default UserReferrals;
